import React, { useState } from 'react'

import { useMutation } from '@apollo/client'

import { AdminCreateSsoEnterprisePlanDocument } from '../../../../__generated__/graphql'
import { client } from '../../../../apollo'

type FormState = {
  orgName: string
  planExpirationDate: string
  planStartDate: string
  orgUrl: string
  slug: string
  scope: string[]
  stagingOrgUrl: string
  domains: string[]
}

export default function EnterpriseSSOForm() {
  const [formData, setFormData] = useState<FormState>({
    orgName: '',
    planExpirationDate: '',
    planStartDate: '',
    orgUrl: '',
    slug: '',
    scope: ['email', 'openid'],
    stagingOrgUrl: 'https://integrator-4546131.okta.com', // Our dev Okta tenant
    domains: [''],
  })

  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<boolean>(false)
  const [submittedData, setSubmittedData] = useState<FormState | null>(null)
  const [enterprisePlanId, setEnterprisePlanId] = useState<number | null>(null)

  const [createSSOEnterprise, { loading }] = useMutation(
    AdminCreateSsoEnterprisePlanDocument,
    {
      onCompleted: (data) => {
        const result = data.adminCreateSsoEnterprisePlan
        if (result.enterprisePlanId) {
          setSuccess(true)
          setEnterprisePlanId(result.enterprisePlanId)
          setSubmittedData({ ...formData })
        }
      },
      onError: (err) => {
        setError(err.message || 'Failed to create SSO enterprise plan')
      },
      client,
    },
  )

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })
  }

  const handleDomainChange = (index: number, value: string) => {
    const updatedDomains = [...formData.domains]
    updatedDomains[index] = value
    setFormData({
      ...formData,
      domains: updatedDomains,
    })
  }

  const addDomainField = () => {
    setFormData({
      ...formData,
      domains: [...formData.domains, ''],
    })
  }

  const removeDomainField = (index: number) => {
    const updatedDomains = [...formData.domains]
    updatedDomains.splice(index, 1)
    setFormData({
      ...formData,
      domains: updatedDomains.length > 0 ? updatedDomains : [''],
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(false)
    setSubmittedData(null)

    // Filter out empty domains
    const filteredDomains = formData.domains.filter(
      (domain) => domain.trim() !== '',
    )

    try {
      await createSSOEnterprise({
        variables: {
          orgName: formData.orgName,
          planExpirationDate: formData.planExpirationDate,
          planStartDate: formData.planStartDate,
          orgUrl: formData.orgUrl,
          slug: formData.slug,
          scope: formData.scope,
          stagingOrgUrl: formData.stagingOrgUrl || undefined,
          domains: filteredDomains.length > 0 ? filteredDomains : undefined,
        },
      })
    } catch (_err: any) {
      // Error handling is done in onError callback
    }
  }

  return (
    <div>
      {success && submittedData && (
        <div>
          <h3>SSO Enterprise plan created successfully!</h3>
          <p>Enterprise Plan ID: {enterprisePlanId}</p>

          <h4>Plan info:</h4>
          <div>
            <div>
              <strong>Organization Name:</strong> {submittedData.orgName}
            </div>
            <div>
              <strong>Plan Start Date:</strong> {submittedData.planStartDate}
            </div>
            <div>
              <strong>Plan Expiration Date:</strong>{' '}
              {submittedData.planExpirationDate}
            </div>
          </div>
          <h4>Next steps:</h4>
          <div>
            <p>
              Coordinate with the enterprise organization to set up their SSO
              app. Their SSO app should be setup at the provided organization
              URL using the provided Request and callback endpoints.
            </p>
            <div>
              <strong>Organization URL:</strong> {submittedData.orgUrl}
            </div>
            <div>
              <strong>Request URL:</strong>{' '}
              {`https://app.blockpartyapp.com/oauth2/${submittedData.slug}/request`}
              <br />
              <strong>Callback URL:</strong>{' '}
              {`https://app.blockpartyapp.com/oauth2/${submittedData.slug}/callback`}
            </div>
            <p>
              The enterprise organization will follow up with us with a client
              ID and secret. We need to add these to the prod and staging
              environments as:
              <br />
              <code>{`${submittedData.slug.toUpperCase().replace('-', '_')}_CLIENT_ID`}</code>
              <br />
              <code>{`${submittedData.slug.toUpperCase().replace('-', '_')}_CLIENT_SECRET`}</code>
            </p>
          </div>
          <h4> Internal Testing: </h4>
          <div>
            <p> Create an SSO app at the staging URL for testing: </p>
            {submittedData.stagingOrgUrl && (
              <div>
                <strong>Staging Organization URL:</strong>{' '}
                {submittedData.stagingOrgUrl}
              </div>
            )}
          </div>
        </div>
      )}

      {error && <div>{error}</div>}

      <form onSubmit={handleSubmit}>
        <div>
          <label htmlFor="orgName">
            Organization Name
            <input
              type="text"
              id="orgName"
              name="orgName"
              value={formData.orgName}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>

        <div>
          <label htmlFor="planStartDate">
            Plan Start Date
            <input
              type="date"
              id="planStartDate"
              name="planStartDate"
              value={formData.planStartDate}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>

        <div>
          <label htmlFor="planExpirationDate">
            Plan Expiration Date
            <input
              type="date"
              id="planExpirationDate"
              name="planExpirationDate"
              value={formData.planExpirationDate}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>

        <div>
          <label htmlFor="orgUrl">
            Organization URL
            <input
              type="text"
              id="orgUrl"
              name="orgUrl"
              placeholder="https://example.okta.com"
              value={formData.orgUrl}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>

        <div>
          <label htmlFor="slug">
            URL Slug (for OAuth endpoints)
            <input
              type="text"
              id="slug"
              name="slug"
              placeholder="okta-example"
              value={formData.slug}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>

        <div>
          <label htmlFor="scope-x">
            Scope &nbsp;
            <input
              type="text"
              id="scope-x"
              name="scope-x"
              value={formData.scope.join(',')}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  scope: e.target.value.split(','),
                }))
              }
              required
            />
          </label>
          &nbsp; comma-separated (current:{' '}
          {`[${formData.scope.map((x) => `"${x}"`).join(', ')}]`})
        </div>

        <div>
          <label htmlFor="stagingOrgUrl">
            Staging Organization URL
            <input
              type="text"
              id="stagingOrgUrl"
              name="stagingOrgUrl"
              placeholder="https://example-staging.okta.com"
              value={formData.stagingOrgUrl}
              onChange={handleInputChange}
            />
          </label>
        </div>

        <div>
          <div>
            <span id="domains-label">Domains</span>
            {formData.domains.map((domain, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`domain-${index}`}>
                <label htmlFor={`domain-${index}`}>
                  <span>Domain {index + 1}</span>
                  <input
                    type="text"
                    id={`domain-${index}`}
                    value={domain}
                    onChange={(e) => handleDomainChange(index, e.target.value)}
                    placeholder="example.com"
                    aria-labelledby="domains-label"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => removeDomainField(index)}
                  disabled={formData.domains.length === 1 && index === 0}
                >
                  Remove
                </button>
              </div>
            ))}
            <button type="button" onClick={addDomainField}>
              Add Domain
            </button>
          </div>
        </div>

        <button type="submit" disabled={loading}>
          {loading ? 'Creating...' : 'Create SSO Enterprise Plan'}
        </button>
      </form>
    </div>
  )
}
